// src/pages/inventory/commerce/components/drawerTabs/CommerceProductDetailsTab.tsx
import { ENV } from '../../../../../config/runtimeEnv';
import React, { useRef, useState, useEffect } from "react";
import { Form, Input, InputNumber, Row, Col, Typography, Upload, Button, Image, Space, Switch, Select, Divider, Popconfirm, Modal } from "antd";
import { UploadOutlined, DeleteOutlined, StarOutlined, StarFilled, DragOutlined, PlusOutlined } from "@ant-design/icons";
import {  uploadProductImages, deleteProductImage, setPrimaryProductImage, reorderProductImages } from "../../../../../api/inventory/productApi";
import type { ProductCategory, Product, SupplementCustomFields as SupplementCustomFieldsType } from "../../../../../types/inventory/ProductTypes";
import ReactQuill from "react-quill";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { SupplementCustomFields } from "../../../shared";
import { useNotifications } from "../../../../../context/NotificationContext";
import { SectionCard } from "../../../../../components/shared";

const { Title, Text } = Typography;

interface CommerceProductDetailsTabProps {
	updatedProduct: Product;
	setUpdatedProduct: React.Dispatch<React.SetStateAction<Product>>;
	handleAutoSave: () => void;
	categories?: ProductCategory[];
}

const CommerceProductDetailsTab: React.FC<CommerceProductDetailsTabProps> = ({ updatedProduct, setUpdatedProduct, handleAutoSave, categories = [] }) => {
	const [imageUploading, setImageUploading] = React.useState(false);
	const [editorKey, setEditorKey] = useState<number>(0);
	const [editorContent, setEditorContent] = useState<string>("");
	const [previewImage, setPreviewImage] = useState<string>("");
	const [previewOpen, setPreviewOpen] = useState(false);
	const [uploadModalVisible, setUploadModalVisible] = useState(false);
	const [fileList, setFileList] = useState<any[]>([]);
	const quillRef = useRef<ReactQuill>(null);
	const { showSuccess, showError } = useNotifications();

	const normalizeForEditor = (html: string): string => {
		if (!html) return "";
		return html.replace(/<p><br><\/p>/gi, "<p>&nbsp;</p>");
	};

	const normalizeForSaving = (html: string): string => {
		if (!html) return "";
		return html.replace(/<p>&nbsp;<\/p>/gi, "<p><br></p>").replace(/<p>\s*<\/p>/gi, "<p><br></p>");
	};

	useEffect(() => {
		const normalizedContent = normalizeForEditor(updatedProduct.description || "");
		setEditorContent(normalizedContent);
	}, [updatedProduct.description]);

	const handleChange = (field: string, value: any) => {
		setUpdatedProduct((prev) => ({ ...prev, [field]: value }));
	};

	const handleDescriptionChange = (value: string) => {
		setEditorContent(value);
		const normalized = normalizeForSaving(value);
		handleChange("description", normalized);
	};

	const handleUploadMultiple = async () => {
		if (!updatedProduct.product_id) {
			showError("Product must be saved before uploading images");
			return;
		}

		if (fileList.length === 0) {
			showError("Please select at least one image");
			return;
		}

		setImageUploading(true);
		try {
			const files = fileList.map((f) => f.originFileObj);
			const response = await uploadProductImages(updatedProduct.product_id, files);

			setUpdatedProduct((prev) => ({
				...prev,
				images: response.product.images,
			}));

			showSuccess(`${fileList.length} image(s) uploaded successfully`);
			setUploadModalVisible(false);
			setFileList([]);
			handleAutoSave();
		} catch (error) {
			console.error("Image upload failed:", error);
			showError("Failed to upload images");
		} finally {
			setImageUploading(false);
		}
	};

	const handleDeleteImage = async (imageId: number) => {
		if (!updatedProduct.product_id) return;

		try {
			await deleteProductImage(updatedProduct.product_id, imageId);
			setUpdatedProduct((prev) => ({
				...prev,
				images: prev.images?.filter((img) => img.id !== imageId),
			}));
			showSuccess("Image deleted successfully");
			handleAutoSave();
		} catch (error) {
			console.error("Failed to delete image:", error);
			showError("Failed to delete image");
		}
	};


	const handleSetPrimary = async (imageId: number) => {
		if (!updatedProduct.product_id) return;

		try {
			const response = await setPrimaryProductImage(updatedProduct.product_id, imageId);
			setUpdatedProduct((prev) => ({
				...prev,
				images: response.product.images,
			}));
			showSuccess("Primary image updated");
			handleAutoSave();
		} catch (error) {
			console.error("Failed to set primary image:", error);
			showError("Failed to set primary image");
		}
	};

	const handleDragEnd = async (result: DropResult) => {
		if (!result.destination || !updatedProduct.images) return;

		const items = Array.from(updatedProduct.images);
		const [reorderedItem] = items.splice(result.source.index, 1);
		items.splice(result.destination.index, 0, reorderedItem);

		// Update local state immediately for smooth UX
		setUpdatedProduct((prev) => ({
			...prev,
			images: items,
		}));

		// Save to backend
		try {
			const imageIds = items.map((img) => img.id);
			await reorderProductImages(updatedProduct.product_id, imageIds);
			showSuccess("Images reordered successfully");
		} catch (error) {
			console.error("Failed to reorder images:", error);
			showError("Failed to reorder images");
			// Revert on error
			handleAutoSave();
		}
	};

	const getImageUrl = (fileId: string) => {
		return `${ENV.VITE_FILE_SERVER}/inventory/display/${fileId}`;
	};

	const calculateMargin = () => {
		if (updatedProduct.unit_price && updatedProduct.unit_cost) {
			const margin = updatedProduct.unit_price - updatedProduct.unit_cost;
			const percentage = (margin / updatedProduct.unit_price) * 100;
			return { margin, percentage };
		}
		return null;
	};

	const marginData = calculateMargin();
	const images = updatedProduct.images || [];
	const primaryImage = images.find((img) => img.is_primary) || images[0];

	const modules = {
		toolbar: [[{ header: [1, 2, 3, false] }], ["bold", "italic", "underline", "strike"], [{ list: "ordered" }, { list: "bullet" }], [{ color: [] }, { background: [] }], ["blockquote"], ["link", "image"], ["clean"]],
		clipboard: {
			matchVisual: false,
		},
	};

	const formats = ["header", "bold", "italic", "underline", "strike", "list", "bullet", "color", "background", "blockquote", "link", "image"];

	return (
		<div>
			<Form layout="vertical">
				<Row gutter={[32, 24]}>
					<Col xs={24} lg={16}>
						<Space direction="vertical" size="large" style={{ width: "100%" }}>
							<div>
								<Title level={5}>Basic Information</Title>
								<Row gutter={[16, 16]}>
									<Col xs={24}>
										<Form.Item label="Product Name" required>
											<Input value={updatedProduct.name || ""} onChange={(e) => handleChange("name", e.target.value)} onBlur={handleAutoSave} placeholder="Enter product name" size="large" />
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item label="Category" required>
											<Select value={updatedProduct.product_category_id || undefined} onChange={(value) => handleChange("product_category_id", value)} onBlur={handleAutoSave} placeholder="Select category" size="large">
												{categories.map((cat) => (
													<Select.Option key={cat.id} value={cat.id}>
														{cat.name}
													</Select.Option>
												))}
											</Select>
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item label="SKU" required>
											<Input value={updatedProduct.sku || ""} onChange={(e) => handleChange("sku", e.target.value)} onBlur={handleAutoSave} placeholder="Enter SKU" size="large" />
										</Form.Item>
									</Col>
									<Col xs={24} md={12}>
										<Form.Item label="Barcode">
											<Input value={updatedProduct.barcode || ""} onChange={(e) => handleChange("barcode", e.target.value)} onBlur={handleAutoSave} placeholder="Enter barcode" />
										</Form.Item>
									</Col>

									<Col xs={24} md={12}>
										<Form.Item label="Status" required>
											<Select value={updatedProduct.status || undefined} onChange={(value) => handleChange("status", value)} onBlur={handleAutoSave} placeholder="Select status" size="large">
												<Select.Option value="active">Active</Select.Option>
												<Select.Option value="draft">Draft</Select.Option>
												<Select.Option value="unlisted">Unlisted</Select.Option>
												<Select.Option value="archived">Archived</Select.Option>
											</Select>
										</Form.Item>
									</Col>
									<Col xs={24}>
										<Form.Item label="Description">
											<ReactQuill
												key={editorKey}
												ref={quillRef}
												theme="snow"
												value={editorContent}
												onChange={handleDescriptionChange}
												onBlur={handleAutoSave}
												style={{
													height: 450,
												}}
												modules={modules}
												formats={formats}
											/>
										</Form.Item>
									</Col>
								</Row>
							</div>
							<Divider />
							<div>
								<Title level={5}>Pricing</Title>
								<Row gutter={[16, 16]}>
									<Col xs={24} md={8}>
										<Form.Item label="Retail Price" required>
											<InputNumber
												style={{ width: "100%" }}
												prefix="$"
												precision={2}
												min={0}
												value={updatedProduct.unit_price || undefined}
												onChange={(value) => handleChange("unit_price", value)}
												onBlur={handleAutoSave}
												placeholder="0.00"
												size="large"
											/>
										</Form.Item>
									</Col>
									<Col xs={24} md={8}>
										<Form.Item label="Cost">
											<InputNumber
												style={{ width: "100%" }}
												prefix="$"
												precision={2}
												min={0}
												value={updatedProduct.unit_cost || undefined}
												onChange={(value) => handleChange("unit_cost", value)}
												onBlur={handleAutoSave}
												placeholder="0.00"
											/>
										</Form.Item>
									</Col>
									{marginData && (
										<Col xs={24} md={8}>
											<Form.Item label="Profit Margin">
												<div
													style={{
														border: "2px solid",
														borderColor: marginData.margin > 0 ? "var(--mg-success)" : "var(--mg-danger)",
														borderRadius: "8px",
														textAlign: "center",
													}}
												>
													<Text
														strong
														style={{
															color: marginData.margin > 0 ? "var(--mg-success)" : "var(--mg-danger)",
															fontSize: 16,
														}}
													>
														${marginData.margin.toFixed(2)}
													</Text>
													<br />
													<Text type="secondary" style={{ fontSize: 12 }}>
														{marginData.percentage.toFixed(1)}% margin
													</Text>
												</div>
											</Form.Item>
										</Col>
									)}
								</Row>
							</div>
							<Divider />
							<div>
								<Title level={5}>Online Store</Title>
								<Row gutter={[16, 16]}>
									<Col xs={24}>
										<Form.Item label="Sync to Shopify">
											<Space>
												<Switch checked={updatedProduct.sync_to_shopify || false} onChange={(checked) => handleChange("sync_to_shopify", checked)} />
												<Text type="secondary">Enable automatic synchronization with Shopify</Text>
											</Space>
										</Form.Item>
									</Col>
								</Row>
							</div>
						</Space>
					</Col>
					<Col xs={24} lg={8}>
						<div style={{ position: "sticky", top: 24 }}>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
								<Title level={5} style={{ margin: 0 }}>
									Product Images
								</Title>
								<Button type="primary" icon={<PlusOutlined />} onClick={() => setUploadModalVisible(true)} disabled={!updatedProduct.product_id}>
									Add Images
								</Button>
							</div>

							{images.length > 0 ? (
								<Space direction="vertical" style={{ width: "100%" }} size="middle">
									{/* Primary Image Display */}
									{primaryImage && (
										<SectionCard title="Primary Image">
											<Image
												width="100%"
												height={250}
												src={getImageUrl(primaryImage.file_id)}
												alt={primaryImage.alt_text || "Product image"}
												style={{ objectFit: "cover", borderRadius: 8 }}
												preview={{
													onVisibleChange: (visible) => {
														if (visible) setPreviewImage(getImageUrl(primaryImage.file_id));
													},
												}}
											/>
										</SectionCard>
									)}

									{/* Image Gallery with Drag & Drop */}
									<SectionCard title={`All Images (${images.length})`}>
										<DragDropContext onDragEnd={handleDragEnd}>
											<Droppable droppableId="images">
												{(provided) => (
													<div {...provided.droppableProps} ref={provided.innerRef}>
														<Space direction="vertical" style={{ width: "100%" }} size="small">
															{images.map((image, index) => (
																<Draggable key={image.id} draggableId={String(image.id)} index={index}>
																	{(provided, snapshot) => (
																		<div
																			ref={provided.innerRef}
																			{...provided.draggableProps}
																			style={{
																				...provided.draggableProps.style,
																				opacity: snapshot.isDragging ? 0.5 : 1,
																			}}
																		>
																			<SectionCard
																				style={{
																					border: image.is_primary ? "2px solid var(--mg-primary)" : "1px solid var(--mg-border)",
																				}}
																			>
																				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
																					<div
																						{...provided.dragHandleProps}
																						style={{
																							cursor: "grab",
																							padding: "0 4px",
																						}}
																					>
																						<DragOutlined />
																					</div>
																					<Image
																						width={60}
																						height={60}
																						src={getImageUrl(image.file_id)}
																						alt={image.alt_text}
																						style={{
																							objectFit: "cover",
																							borderRadius: 4,
																						}}
																						preview={false}
																						onClick={() => {
																							setPreviewImage(getImageUrl(image.file_id));
																							setPreviewOpen(true);
																						}}
																					/>
																					<div style={{ flex: 1 }}>
																						<Text strong style={{ fontSize: 12 }}>
																							Position {index + 1}
																						</Text>
																						<br />
																						<Text type="secondary" style={{ fontSize: 11 }}>
																							{image.alt_text || "No alt text"}
																						</Text>
																					</div>
																					<Space direction="vertical" size="small">
																						<Button
																							type={image.is_primary ? "primary" : "default"}
																							size="small"
																							icon={image.is_primary ? <StarFilled /> : <StarOutlined />}
																							onClick={() => handleSetPrimary(image.id)}
																							title="Set as primary"
																						/>
																						<Popconfirm title="Delete this image?" onConfirm={() => handleDeleteImage(image.id)} okText="Delete" okType="danger">
																							<Button danger size="small" icon={<DeleteOutlined />} />
																						</Popconfirm>
																					</Space>
																				</div>
																			</SectionCard>
																		</div>
																	)}
																</Draggable>
															))}
															{provided.placeholder}
														</Space>
													</div>
												)}
											</Droppable>
										</DragDropContext>
									</SectionCard>
								</Space>
							) : (
								<SectionCard>
									<div
										style={{
											textAlign: "center",
											padding: "40px 20px",
											border: "2px dashed var(--mg-border)",
											borderRadius: 8,
										}}
									>
										<Text type="secondary">No images yet</Text>
										<br />
										<Button type="link" onClick={() => setUploadModalVisible(true)} disabled={!updatedProduct.product_id}>
											Upload your first image
										</Button>
									</div>
								</SectionCard>
							)}

							<Divider />
							<Title level={5}>Metadata</Title>
							<Space direction="vertical" style={{ width: "100%" }}>
								<Text type="secondary">
									<strong>Created:</strong> {updatedProduct.created_at ? new Date(updatedProduct.created_at).toLocaleString() : "—"}
								</Text>
								<Text type="secondary">
									<strong>Updated:</strong> {updatedProduct.updated_at ? new Date(updatedProduct.updated_at).toLocaleString() : "—"}
								</Text>
							</Space>
						</div>
					</Col>
				</Row>
			</Form>

			{/* Supplement Custom Fields - show only for Supplements & Nutraceuticals category */}
			{categories.find(cat => cat.id === updatedProduct.product_category_id)?.name === 'Supplements & Nutraceuticals' && (
				<SupplementCustomFields
					customFields={updatedProduct.custom_fields as SupplementCustomFieldsType}
					isEditing={true}
					onFieldChange={(fieldName, value) => {
						const updatedCustomFields = {
							...(updatedProduct.custom_fields as SupplementCustomFieldsType || {}),
							[fieldName]: value
						};
						setUpdatedProduct(prev => ({
							...prev,
							custom_fields: updatedCustomFields
						}));
						handleAutoSave();
					}}
				/>
			)}

			{/* Upload Modal */}
			<Modal
				title="Upload Product Images"
				open={uploadModalVisible}
				onCancel={() => {
					setUploadModalVisible(false);
					setFileList([]);
				}}
				onOk={handleUploadMultiple}
				okText="Upload"
				confirmLoading={imageUploading}
				width={600}
			>
				<Space direction="vertical" style={{ width: "100%" }} size="large">
					<Upload.Dragger multiple accept="image/*" fileList={fileList} onChange={({ fileList }) => setFileList(fileList)} beforeUpload={() => false} listType="picture">
						<p className="ant-upload-drag-icon">
							<UploadOutlined style={{ fontSize: 48, color: "var(--mg-primary)" }} />
						</p>
						<p className="ant-upload-text">Click or drag images to upload</p>
						<p className="ant-upload-hint">Support for single or bulk upload. Select multiple images to upload at once.</p>
					</Upload.Dragger>
					<Text type="secondary">You can upload up to 10 images at once. Images will be automatically optimized.</Text>
				</Space>
			</Modal>

			{/* Image Preview Modal */}
			<Image
				style={{ display: "none" }}
				preview={{
					visible: previewOpen,
					src: previewImage,
					onVisibleChange: (visible) => setPreviewOpen(visible),
				}}
			/>
		</div>
	);
};

export default CommerceProductDetailsTab;
